import { rollbar } from "../../rollbar";
/** Debounced iframe height posts: avoids stacked timers and parent/child resize feedback loops. */
let lastPostedFrameHeight: number | null = null;
/** Browser timers are numeric IDs; avoid NodeJS.Timeout from @types/node mismatch. */
let iframeResizePostTimer: number | null = null;
let pendingIframeResizeOrigin = "*";
let pendingIframeResizeHeight: number | undefined;

let lastPostedFrameHeightScaled: number | null = null;
let iframeResizeToPixelsPostTimer: number | null = null;
let lastParentPostMessageKey: string | null = null;

/**
 * Ignore sub-scrollbar noise (~12–16px) from gutter/overflow flicker.
 * Real layout changes (e.g. tariffs loaded) are hundreds/thousands of px.
 */
const FRAME_HEIGHT_MIN_DELTA_PX = 16;
/**
 * Content height of `#app` only — never `html`/`body` scrollHeight.
 * Prefer `scrollHeight` (real content). `offsetHeight` can reflect `min-height: 100%`
 * (iframe viewport) and create parent↔child resize feedback loops.
 */
function measureAppContentHeight(): number | undefined {
  const app = document.getElementById("app");

  if (!app) {
    return undefined;
  }

  const contentHeight = app.scrollHeight;
  const height =
    typeof contentHeight === "number" && contentHeight > 0
      ? contentHeight
      : app.offsetHeight;

  if (typeof height !== "number" || height <= 0) {
    return undefined;
  }

  return Math.ceil(height);
}

function postIframeFrameHeightMessage(
  allowedOrigin: string,
  iframeHeight?: number,
): void {
  const measuredHeight =
    typeof iframeHeight === "number"
      ? Math.ceil(iframeHeight)
      : measureAppContentHeight();

  if (measuredHeight === undefined || Number.isNaN(measuredHeight)) {
    return;
  }

  if (
    lastPostedFrameHeight !== null &&
    Math.abs(lastPostedFrameHeight - measuredHeight) <=
      FRAME_HEIGHT_MIN_DELTA_PX
  ) {
    return;
  }

  lastPostedFrameHeight = measuredHeight;

  if (typeof window === "undefined" || window.parent === window) {
    return;
  }

  window.parent.postMessage({ frameHeight: measuredHeight }, allowedOrigin);
}

export function iFrameResize(allowedOrigin = "*", iframeHeight?: number): void {
  pendingIframeResizeOrigin = allowedOrigin;
  pendingIframeResizeHeight = iframeHeight;

  if (iframeResizePostTimer !== null) {
    window.clearTimeout(iframeResizePostTimer);
  }

  iframeResizePostTimer = window.setTimeout(() => {
    iframeResizePostTimer = null;

    const origin = pendingIframeResizeOrigin;
    const fixedHeight = pendingIframeResizeHeight;

    postIframeFrameHeightMessage(origin, fixedHeight);
  }, 1000);
}
/**
 * Post current `#app` height to the parent immediately (no 1s debounce).
 * Cancels a pending debounced `iFrameResize` so the parent does not get a duplicate late message.
 * Use when layout is known to have updated (e.g. search field skeleton visible).
 */
export function iFrameResizeNow(
  allowedOrigin = "*",
  iframeHeight?: number,
): void {
  if (iframeResizePostTimer !== null) {
    window.clearTimeout(iframeResizePostTimer);
    iframeResizePostTimer = null;
  }

  postIframeFrameHeightMessage(allowedOrigin, iframeHeight);
}

export function iFrameResizeToPixels(): void {
  if (iframeResizeToPixelsPostTimer !== null) {
    window.clearTimeout(iframeResizeToPixelsPostTimer);
  }

  iframeResizeToPixelsPostTimer = window.setTimeout(() => {
    iframeResizeToPixelsPostTimer = null;

    const height = measureAppContentHeight();

    if (!height) {
      return;
    }

    const heightInPixels = height * 17;

    if (
      lastPostedFrameHeightScaled !== null &&
      Math.abs(lastPostedFrameHeightScaled - heightInPixels) <= 1
    ) {
      return;
    }

    lastPostedFrameHeightScaled = heightInPixels;

    if (typeof window === "undefined" || window.parent === window) {
      return;
    }

    window.parent.postMessage({ frameHeight: heightInPixels }, "*");
  }, 1000);
}

export function postMessageWrapper(
  message: Record<string, unknown>,
  origin: string,
  toParentWindow: boolean,
): void {
  if (!toParentWindow) {
    window.postMessage(message, origin);
    return;
  }

  if (typeof window === "undefined" || window.parent === window) {
    return;
  }

  let messageKey: string | null = null;

  try {
    messageKey = `${origin}::${JSON.stringify(message)}`;
  } catch (error) {
    rollbar?.error(error as Error, {
      context: "iframeCommunication:postMessageWrapper",
    });
  }

  if (messageKey && messageKey === lastParentPostMessageKey) {
    return;
  }

  lastParentPostMessageKey = messageKey;
  window.parent.postMessage(message, origin);
}
